"use client";

import InstagramManagementCard, {
  type InstagramManagementValue,
} from '@/components/admin/instagram/InstagramManagementCard';
import * as Dialog from '@radix-ui/react-dialog';
import { Instagram, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';

type ToastFn = (message: string, type: 'success' | 'error' | 'info') => void;

export type InstagramPostingModalItem = {
  _id: string;
  title: string;
  excerpt?: string;
  featuredImage?: string;
  tags?: string[];
  instagramPostingCaption?: string;
  instagramPostingTags?: string[];
  instagramPostingUseFeaturedImage?: boolean;
  instagramPostingImageUrl?: string;
  instagramPostingScheduledAt?: string | null;
  instagramPostingStatus?: string;
  instagramPostingPublishedAt?: string | null;
  instagramMediaId?: string;
  instagramPermalink?: string;
  instagramUsername?: string;
  instagramLastSyncAt?: string | null;
};

function toDatetimeLocalValue(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function validateInstagramValue(value: InstagramManagementValue, featuredImage: string) {
  const errors: Record<string, string> = {};
  const imageUrl = value.imageUrl.trim();
  if (!value.caption.trim()) {
    errors.caption = 'Instagram caption is required.';
  }
  if (!value.useFeaturedImage && !imageUrl) {
    errors.image = 'Instagram image is required.';
  }
  if (value.useFeaturedImage && !featuredImage.trim() && !imageUrl) {
    errors.image = 'Add a featured image or provide a separate Instagram image URL.';
  }
  if (imageUrl) {
    try {
      const parsed = new URL(imageUrl);
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        errors.image = 'Use a public HTTP or HTTPS image URL for Instagram.';
      }
    } catch {
      errors.image = 'Use a valid public image URL for Instagram.';
    }
  }
  if (value.publishType === 'scheduled' && !value.scheduledAt) {
    errors.schedule = 'Schedule date and time are required.';
  }
  return errors;
}

export default function InstagramPostingModal({
  item,
  open,
  onOpenChange,
  showToast,
  onSaved,
  contentKind = 'thought_leadership',
}: {
  item: InstagramPostingModalItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showToast: ToastFn;
  onSaved: () => Promise<void> | void;
  contentKind?: 'blog' | 'thought_leadership' | 'social_media';
}) {
  const isSocialMediaPost = contentKind === 'social_media';
  const [value, setValue] = useState<InstagramManagementValue>({
    enabled: true,
    publishType: 'now',
    caption: '',
    tagsStr: '',
    useFeaturedImage: true,
    imageUrl: '',
    scheduledAt: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const featuredImage = item?.featuredImage || '';
  const subtitle = isSocialMediaPost
    ? 'Create a standalone Instagram post'
    : item?.title
    ? item.title.length > 56
      ? `${item.title.slice(0, 55)}...`
      : item.title
    : 'Configure an Instagram post';

  useEffect(() => {
    if (!open || !item) return;
    setErrors({});
    setValue({
      enabled: true,
      publishType: 'now',
      caption: item.instagramPostingCaption ?? item.excerpt ?? item.title ?? '',
      tagsStr: (item.instagramPostingTags?.length ? item.instagramPostingTags : item.tags || []).join(', '),
      useFeaturedImage: item.instagramPostingUseFeaturedImage !== false,
      imageUrl: item.instagramPostingImageUrl || '',
      scheduledAt: toDatetimeLocalValue(item.instagramPostingScheduledAt),
      status: item.instagramPostingStatus || 'draft',
      publishedAt: item.instagramPostingPublishedAt || '',
      mediaId: item.instagramMediaId || '',
      permalink: item.instagramPermalink || '',
      username: item.instagramUsername || '',
      lastSyncAt: item.instagramLastSyncAt || '',
    });
  }, [open, item]);

  const submit = async () => {
    if (!item?._id) return;
    const nextErrors = validateInstagramValue(value, featuredImage);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const tags = value.tagsStr
      .split(/[,\n]/)
      .map((tag) => tag.trim())
      .filter(Boolean);

    if (isSocialMediaPost) {
      const imageUrl = value.imageUrl.trim() || (value.useFeaturedImage ? featuredImage.trim() : '');
      const form = new FormData();
      form.append('platform', 'instagram');
      form.append('postType', 'photo');
      form.append('status', 'published');
      form.append('message', value.caption);
      form.append('tags', JSON.stringify(tags));
      form.append('links', JSON.stringify([]));
      form.append('image_url', imageUrl);

      setSaving(true);
      try {
        const r = await fetch('/api/admin/social-media/posts', {
          method: 'POST',
          cache: 'no-store',
          body: form,
        });
        const d = (await r.json()) as { success?: boolean; error?: string };
        if (!r.ok || !d.success) {
          throw new Error(d.error || 'Instagram publish failed');
        }
        showToast('Instagram post published.', 'success');
        onOpenChange(false);
        await onSaved();
      } catch (error) {
        showToast(error instanceof Error ? error.message : 'Instagram publish failed', 'error');
      } finally {
        setSaving(false);
      }
      return;
    }

    const body = {
      instagramPostingEnabled: true,
      instagramPublishType: value.publishType,
      instagramPostingCaption: value.caption,
      instagramPostingTags: tags,
      instagramPostingUseFeaturedImage: value.useFeaturedImage,
      instagramPostingImageUrl: value.imageUrl.trim(),
      instagramPostingScheduledAt:
        value.publishType === 'scheduled' && value.scheduledAt
          ? new Date(value.scheduledAt).toISOString()
          : null,
    };

    setSaving(true);
    try {
      const basePath =
        contentKind === 'blog'
          ? `/api/admin/blogs/${item._id}/instagram-post`
          : `/api/admin/thought-leadership/${item._id}/instagram-post`;
      const r = await fetch(basePath, {
        method: value.publishType === 'now' ? 'POST' : 'PATCH',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = (await r.json()) as { success?: boolean; error?: string };
      if (!r.ok || !d.success) {
        throw new Error(d.error || 'Instagram save failed');
      }
      showToast(
        value.publishType === 'now' ? 'Instagram post published.' : 'Instagram post scheduled.',
        'success'
      );
      onOpenChange(false);
      await onSaved();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Instagram save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[92vh] w-[min(680px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-2xl">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-pink-500 via-rose-500 to-amber-400 text-white">
                <Instagram className="h-5 w-5" />
              </div>
              <div>
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  Instagram Post Scheduler
                </Dialog.Title>
                <Dialog.Description className="text-sm text-gray-500">
                  {subtitle}
                </Dialog.Description>
              </div>
            </div>
            <Dialog.Close
              className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
              aria-label="Close Instagram post scheduler"
            >
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>

          <div className="px-5 pb-5">
            <InstagramManagementCard
              featuredImage={featuredImage}
              value={value}
              onChange={(next) => {
                setErrors({});
                setValue(next);
              }}
              validationErrors={errors}
              showToast={showToast}
              allowPublishTypeChoice
              {...(isSocialMediaPost ? { publishTypeOptions: ['now' as const] } : {})}
            />
          </div>

          <div className="sticky bottom-0 flex justify-end gap-2 border-t border-gray-200 bg-white px-5 py-3">
            <Dialog.Close className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              Close
            </Dialog.Close>
            <button
              type="button"
              disabled={saving}
              onClick={submit}
              className="inline-flex items-center gap-2 rounded-md border border-pink-200 bg-pink-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-pink-700 disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {value.publishType === 'now' ? 'Publish Now' : 'Save Schedule'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
